export type PromoToken = {
  id: string;
  tripId: string;
  tripTitle: string;
  ownerEmail: string;
  createdAt: string;
  isPromo: boolean;
  tag: string | null;
  demoAuthorId: string | null;
  demoAuthorName: string | null;
  photoUrl: string | null;
};

export type PromoTrip = {
  tokenId: string;
  tripId: string;
  title: string;
  description: string | null;
  startLocation: string | null;
  endLocation: string | null;
  coverPhoto: string | null;
  stops: number;
  days: number | null;
  ownerName?: string;
  ownerImage?: string | null;
};

export type PromoDestination = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  position: number;
  type: "home" | "destination";
  photoUrl: string | null;
};

export type PromoSegment = {
  id: string;
  fromDestinationId: string;
  toDestinationId: string;
  mode: "plane" | "car" | "train";
};

export type PromoRoute = {
  segmentId: string;
  fromDestinationId: string;
  toDestinationId: string;
  mode: "plane" | "car" | "train";
  polyline: Array<[number, number] | [number, number, number]>;
  distanceM: number | null;
  durationSec: number | null;
  cacheHit: boolean;
  source?: "geoapify" | "osrm" | "great-circle";
};

export type PromoTripWithMap = PromoTrip & {
  destinations: PromoDestination[];
  segments: PromoSegment[];
  routes: PromoRoute[];
};

export async function listPromoTokens(search?: string): Promise<PromoToken[]> {
  const params = search ? `?search=${encodeURIComponent(search)}` : "";
  const res = await fetch(`/api/admin/promo${params}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { tokens: PromoToken[] };
  return body.tokens;
}

export async function setTokenPromo(
  tokenId: string,
  isPromo: boolean
): Promise<PromoToken> {
  const res = await fetch(`/api/admin/promo/${tokenId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isPromo }),
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { token: PromoToken };
  return body.token;
}

export async function regeneratePromoTripPhoto(
  tokenId: string,
): Promise<{ photoUrl: string | null }> {
  const res = await fetch(`/api/admin/promo/${tokenId}/photo/regenerate`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { trip: { id: string; photoUrl: string | null } };
  return { photoUrl: body.trip.photoUrl };
}

export async function setTokenTag(
  tokenId: string,
  tag: string,
): Promise<PromoToken> {
  const res = await fetch(`/api/admin/promo/${tokenId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isPromo: true, tag }),
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { token: PromoToken };
  return body.token;
}

// ── User administration ───────────────────────────────────

export type AdminUser = {
  id: string;
  name: string;
  email: string;
  image: string | null;
  nickname: string | null;
  isAdmin: boolean;
  isBlocked: boolean;
  isPaused: boolean;
  subscriptionLevel: number;
  subscriptionStartedAt: string | null;
  subscriptionValidUntil: string | null;
  tripCount: number;
  lastLogin: string | null;
  createdAt: string;
  langfuseUrl?: string;
};

export type AdminUsersResponse = {
  users: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
};

export type AdminUserTrip = {
  id: string;
  title: string;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
};

export async function listAdminUsers(
  page = 1,
  pageSize = 20,
  q?: string,
  sort?: string,
  dir?: string,
): Promise<AdminUsersResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (q) params.set("q", q);
  if (sort) params.set("sort", sort);
  if (dir) params.set("dir", dir);
  const res = await fetch(`/api/admin/users?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return (await res.json()) as AdminUsersResponse;
}

export async function listAdminUserTrips(userId: string): Promise<AdminUserTrip[]> {
  const res = await fetch(`/api/admin/users/${userId}/trips`, { credentials: "include" });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { trips: AdminUserTrip[] };
  return body.trips;
}

export async function blockUser(userId: string): Promise<void> {
  const res = await fetch(`/api/admin/users/${userId}/block`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json()) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
}

export async function unblockUser(userId: string): Promise<void> {
  const res = await fetch(`/api/admin/users/${userId}/unblock`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
}

export async function pauseUser(userId: string): Promise<void> {
  const res = await fetch(`/api/admin/users/${userId}/pause`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json()) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
}

export async function unpauseUser(userId: string): Promise<void> {
  const res = await fetch(`/api/admin/users/${userId}/unpause`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
}

export async function setSubscription(
  userId: string,
  level: number,
  validUntil?: string | null,
): Promise<void> {
  const res = await fetch(`/api/admin/users/${userId}/subscribe`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ level, validUntil: validUntil ?? null }),
  });
  if (!res.ok) {
    const body = (await res.json()) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
}

export async function removeSubscription(userId: string): Promise<void> {
  const res = await fetch(`/api/admin/users/${userId}/unsubscribe`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
}

export type AdminConfig = {
  config: Record<string, string>;
  userCount: number;
  anonDailyTurnBudgetUsed: number;
};

export async function getAdminConfig(): Promise<AdminConfig> {
  const res = await fetch("/api/admin/config", { credentials: "include" });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return (await res.json()) as AdminConfig;
}

export async function setAdminConfig(key: string, value: string): Promise<void> {
  const res = await fetch("/api/admin/config", {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key, value }),
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
}

export async function createUser(data: {
  name: string;
  email: string;
  password: string;
  emailVerified: boolean;
}): Promise<{ id: string; name: string; email: string }> {
  const res = await fetch("/api/admin/users", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = (await res.json()) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  const body = (await res.json()) as { user: { id: string; name: string; email: string } };
  return body.user;
}

export async function impersonateUser(userId: string): Promise<void> {
  const res = await fetch(`/api/admin/users/${userId}/impersonate`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json()) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
}

// ── Trip administration ──────────────────────────────────

export type AdminTrip = {
  id: string;
  title: string;
  ownerEmail: string;
  ownerId: string;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
  langfuseUrl?: string;
};

export type AdminTripsResponse = {
  trips: AdminTrip[];
  total: number;
  page: number;
  pageSize: number;
};

export async function listAdminTrips(
  page = 1,
  pageSize = 20,
  q?: string,
  sort?: string,
  dir?: string,
): Promise<AdminTripsResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (q) params.set("q", q);
  if (sort) params.set("sort", sort);
  if (dir) params.set("dir", dir);
  const res = await fetch(`/api/admin/trips?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return (await res.json()) as AdminTripsResponse;
}

// ── Demo author administration ───────────────────────────

export type DemoAuthor = {
  id: string;
  firstName: string;
  lastName: string;
  image: string | null;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
  tripCount: number;
  trips: { id: string; title: string }[];
};

export async function listDemoAuthors(): Promise<DemoAuthor[]> {
  const res = await fetch("/api/admin/demo-authors", { credentials: "include" });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { demoAuthors: DemoAuthor[] };
  return body.demoAuthors;
}

export async function createDemoAuthor(data: {
  firstName: string;
  lastName: string;
  comment?: string;
}): Promise<DemoAuthor> {
  const res = await fetch("/api/admin/demo-authors", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = (await res.json()) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  const body = (await res.json()) as { demoAuthor: DemoAuthor };
  return body.demoAuthor;
}

export async function updateDemoAuthor(
  id: string,
  data: { firstName?: string; lastName?: string; comment?: string },
): Promise<DemoAuthor> {
  const res = await fetch(`/api/admin/demo-authors/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { demoAuthor: DemoAuthor };
  return body.demoAuthor;
}

export async function deleteUser(id: string): Promise<void> {
  const res = await fetch(`/api/admin/users/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
}

export async function deleteDemoAuthor(id: string): Promise<void> {
  const res = await fetch(`/api/admin/demo-authors/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
}

export async function uploadDemoAuthorPhoto(
  id: string,
  file: Blob,
): Promise<string> {
  const res = await fetch(`/api/admin/demo-authors/${id}/photo`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!res.ok) {
    const body = (await res.json()) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  const body = (await res.json()) as { imageUrl: string };
  return body.imageUrl;
}

export async function attachDemoAuthor(
  tripId: string,
  demoAuthorId: string | null,
): Promise<void> {
  const res = await fetch("/api/admin/demo-authors/attach", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tripId, demoAuthorId }),
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
}

// ── Model availability ──────────────────────────────────

export type ModelAvailabilityEntry = {
  role: string;
  modelId: string;
  isPrimary: boolean;
  available: boolean;
  name: string | null;
  contextLength: number | null;
  promptPrice: string | null;
  completionPrice: string | null;
};

export async function listModels(): Promise<ModelAvailabilityEntry[]> {
  const res = await fetch("/api/admin/models", { credentials: "include" });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { models: ModelAvailabilityEntry[] };
  return body.models;
}

// ── Viator taxonomy ──────────────────────────────────────

export type ViatorTaxonomyStatus = {
  destinations: { count: number; refreshedAt: string | null };
  tags: { count: number; refreshedAt: string | null };
  configured: boolean;
};

export type ViatorTaxonomyRefreshResponse = ViatorTaxonomyStatus & {
  refreshed: {
    destinations: number;
    tags: number;
    errors?: { destinations?: string; tags?: string };
  };
};

export async function getViatorTaxonomyStatus(): Promise<ViatorTaxonomyStatus> {
  const res = await fetch("/api/admin/viator/taxonomy", { credentials: "include" });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return (await res.json()) as ViatorTaxonomyStatus;
}

export async function refreshViatorTaxonomy(): Promise<ViatorTaxonomyRefreshResponse> {
  const res = await fetch("/api/admin/viator/taxonomy/refresh", {
    method: "POST",
    credentials: "include",
  });
  if (res.status === 400) {
    const body = (await res.json()) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  if (res.status === 502) {
    return (await res.json()) as ViatorTaxonomyRefreshResponse;
  }
  if (!res.ok) {
    throw new Error(`Request failed: ${res.status}`);
  }
  return (await res.json()) as ViatorTaxonomyRefreshResponse;
}

// ── Usage / consumption ─────────────────────────────────

export type VendorAggregate = {
  vendor: string;
  totalCalls: number;
  cacheHits: number;
  totalCost: number;
  kinds: Record<string, { calls: number; cacheHits: number }>;
  dashboardUrl?: string;
};

export type TopTripAggregate = {
  tripId: string;
  totalCalls: number;
  totalCost: number;
  title?: string;
};

export type TopUserAggregate = {
  userId: string;
  totalCalls: number;
  totalCost: number;
  name?: string;
  email?: string;
};

export type UsageResponse = {
  usage: VendorAggregate[];
  topTrips?: TopTripAggregate[];
  topUsers?: TopUserAggregate[];
  vendorLinks: Record<string, string>;
  from?: string;
  to?: string;
  error?: string;
};

export async function fetchUsage(
  from: string,
  to: string,
  opts?: { tripId?: string; userId?: string },
): Promise<UsageResponse> {
  const params = new URLSearchParams({ from, to });
  if (opts?.tripId) params.set("tripId", opts.tripId);
  if (opts?.userId) params.set("userId", opts.userId);
  const res = await fetch(`/api/admin/usage?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return (await res.json()) as UsageResponse;
}

// ── Route failures ─────────────────────────────────────

export type RouteFailureAggregate = {
  id: number;
  fromName: string;
  toName: string;
  fromLat: number;
  fromLng: number;
  toLat: number;
  toLng: number;
  mode: string;
  reason: string;
  count: number;
  firstSeen: string;
  lastSeen: string;
  lastStatus: number | null;
  lastUrl: string | null;
  langfuseUrl?: string;
};

export type RouteFailuresResponse = {
  failures: RouteFailureAggregate[];
};

export async function fetchRouteFailures(): Promise<RouteFailuresResponse> {
  const res = await fetch("/api/admin/route-failures", { credentials: "include" });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return (await res.json()) as RouteFailuresResponse;
}

// ── Promo photo generation ─────────────────────────────

export type ImageModelOption = {
  id: string;
  label: string;
  provider: "workers-ai" | "openrouter";
};

export async function listImageModels(): Promise<ImageModelOption[]> {
  const res = await fetch("/api/admin/promo/models", { credentials: "include" });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { models: ImageModelOption[] };
  return body.models;
}

export async function generateCustomPromoPhoto(
  tokenId: string,
  prompt: string,
  model: string,
): Promise<{ previewUrl: string | null }> {
  const res = await fetch(`/api/admin/promo/${tokenId}/photo`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt, model }),
  });
  if (!res.ok) {
    const body = (await res.json()) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  const body = (await res.json()) as { trip: { id: string; previewUrl: string | null } };
  return { previewUrl: body.trip.previewUrl };
}

export async function commitCustomPromoPhoto(
  tokenId: string,
): Promise<{ photoUrl: string | null }> {
  const res = await fetch(`/api/admin/promo/${tokenId}/photo/commit`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json()) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  const body = (await res.json()) as { trip: { id: string; photoUrl: string | null } };
  return { photoUrl: body.trip.photoUrl };
}

// ── Promo trips ─────────────────────────────────────────

export async function listPromoTrips(): Promise<PromoTrip[]> {
  const res = await fetch("/api/promo-trips", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { promoTrips: PromoTrip[] };
  return body.promoTrips;
}

export async function getRandomPromoTrips(
  limit = 4,
  tag?: string,
): Promise<PromoTripWithMap[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (tag) params.set("tag", tag);
  const res = await fetch(`/api/promo-trips/random?${params}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { promoTrips: PromoTripWithMap[] };
  return body.promoTrips;
}
